import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, HardHat, MapPin, Layers, Clock } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { HERO_SEQUENCE, HERO_VIDEO_URL, IMAGES } from '@/data/constants';
import { MarketplaceStats, compactMoney, budgetValueNote } from '@/lib/homeStats';
import { CountUp, prefersReducedMotion } from '@/components/home/Reveal';

interface Props {
  stats: MarketplaceStats;
  /** Anchor id of the "How it works" section to scroll to. */
  howItWorksId: string;
}

/**
 * Cinematic hero. Renders the licensed brand film when HERO_VIDEO_URL is set,
 * otherwise cross-fades the four-frame HERO_SEQUENCE with a slow Ken Burns
 * push. Both paths stop entirely under prefers-reduced-motion.
 */
const HeroVideo: React.FC<Props> = ({ stats, howItWorksId }) => {
  const { t, path, lang } = useI18n();
  const [frame, setFrame] = useState(0);
  const reduced = prefersReducedMotion();
  const useVideo = Boolean(HERO_VIDEO_URL);

  useEffect(() => {
    if (useVideo || reduced || HERO_SEQUENCE.length < 2) return;
    const id = window.setInterval(() => {
      setFrame((f) => (f + 1) % HERO_SEQUENCE.length);
    }, 5200);
    return () => window.clearInterval(id);
  }, [useVideo, reduced]);

  const budget = compactMoney(stats.postedBudgetValue);

  // Only tiles backed by a real, computable figure are rendered. A tile with no
  // data disappears instead of being padded with an invented number.
  const tiles = [
    stats.activeProjects > 0 && {
      key: 'projects',
      icon: Layers,
      value: <CountUp value={stats.activeProjects} />,
      label: t('home.statActiveProjects'),
      note: '',
    },
    stats.statesCovered > 0 && {
      key: 'states',
      icon: MapPin,
      value: <CountUp value={stats.statesCovered} />,
      label: t('home.statStates'),
      note: '',
    },
    {
      key: 'trades',
      icon: HardHat,
      value: <CountUp value={stats.tradesCovered} />,
      label: t('home.statTrades'),
      note: '',
    },
    budget
      ? {
          key: 'budget',
          icon: Clock,
          value: budget,
          label: t('home.statPostedValue'),
          /* The budget qualifier is NOT printed inside the tile. It is shown
             once, directly beneath the stat grid, so the same sentence never
             appears twice on the page. */
          note: '',
        }
      : null,

  ].filter(Boolean) as {
    key: string;
    icon: typeof Layers;
    value: React.ReactNode;
    label: string;
    note: string;
  }[];

  return (
    <section className="relative isolate overflow-hidden bg-brand-navy">
      {/* ---------- BACKDROP ---------- */}
      <div className="absolute inset-0" aria-hidden="true">
        {useVideo ? (
          <video
            className="h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster={IMAGES.hero}
          >
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
        ) : (
          HERO_SEQUENCE.map((f, i) => (
            <img
              key={f.src}
              src={f.src}
              alt=""
              /* First frame is the LCP image: eager + high priority. Rest lazy. */
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'low'}
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1600ms] ease-in-out ${
                i === frame ? 'opacity-100' : 'opacity-0'
              } ${i === frame && !reduced ? 'animate-ken-burns' : ''}`}
            />
          ))
        )}
      </div>

      {/* Depth: navy wash left -> transparent right, plus a warm orange glow. */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/90 to-brand-navy/45"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-brand-orange/25 blur-[130px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-[380px] w-[380px] rounded-full bg-brand-blue/20 blur-[120px]"
        aria-hidden="true"
      />

      {/* ---------- CONTENT ---------- */}
      <div className="relative mx-auto flex min-h-[520px] max-w-7xl flex-col justify-center px-4 py-16 sm:min-h-[560px] sm:px-6 sm:py-20 lg:min-h-[600px] lg:px-8">
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-brand-orange/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-orange-200 ring-1 ring-brand-orange/40 backdrop-blur">
          <HardHat className="h-3.5 w-3.5" /> {t('home.heroBadge')}
        </span>

        <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {t('home.heroTitleV2')}
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
          {t('home.heroSubV2')}
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to={path('/jobs')}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange px-6 py-4 text-base font-bold text-white shadow-glow transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-300"
          >
            {t('home.ctaBrowseProjects')} <ArrowRight className="h-5 w-5" />
          </Link>
          <a
            href={`#${howItWorksId}`}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-6 py-4 text-base font-bold text-white backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/20"
          >
            <PlayCircle className="h-5 w-5" /> {t('home.ctaHowItWorks')}
          </a>
        </div>

        {/* ---------- TRUST STATS (all derived from live listings) ---------- */}
        {tiles.length > 0 && (
          <dl className="mt-12 grid max-w-4xl grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {tiles.map((tile) => (
              <div
                key={tile.key}
                className="rounded-2xl border border-white/10 bg-white/[0.07] p-4 backdrop-blur-sm transition-colors hover:border-white/20"
              >
                <tile.icon className="h-5 w-5 text-brand-orange" aria-hidden="true" />
                <dd className="mt-2 text-2xl font-extrabold tabular-nums text-white sm:text-3xl">
                  {tile.value}
                </dd>
                <dt className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-300">
                  {tile.label}
                </dt>
                {tile.note && <p className="mt-1.5 text-[11px] leading-snug text-slate-400">{tile.note}</p>}
              </div>
            ))}
          </dl>
        )}

        {/* ONE disclaimer, directly under the hero statistics. This is the only
            place on the home page where the budget qualifier and the data
            source are stated — the duplicate copies that used to sit in the
            tile and in the "marketplace at a glance" strip are gone. */}
        <p className="mt-4 max-w-2xl text-xs leading-relaxed text-slate-400">
          {t('home.statsSource')}
          {budget ? ` ${budgetValueNote(stats, lang)}` : ''}
        </p>

      </div>
    </section>
  );
};

export default HeroVideo;
